import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  SectionHeader,
} from "../../../../components/ui";
import { useShipmentsList } from "../../../../features/shipments/queries";
import { useSmartTrackingConfig } from "../../../../features/smartTracking/queries";
import { useUpdateSmartTrackingConfig } from "../../../../features/smartTracking/mutations";
import {
  DEFAULT_SMART_TRACKING_CONFIG,
  sameSmartTrackingConfig,
  SMART_TRACKING_RULES,
  SMART_TRACKING_RULE_IDS,
} from "../../../../features/smartTracking/types";
import type {
  SmartTrackingConfig,
  SmartTrackingConfigInput,
  SmartTrackingRuleId,
} from "../../../../features/smartTracking/types";
import { eventTypeTone } from "../../statusTones";
import SmartSwitch from "./components/SmartSwitch";

const SHIPMENTS_SAMPLE = 100;

export default function SmartTrackingView() {
  const { t } = useTranslation("envios");
  const configQuery = useSmartTrackingConfig();
  const shipmentsQuery = useShipmentsList({ page: 1, limit: SHIPMENTS_SAMPLE });
  const update = useUpdateSmartTrackingConfig();

  const [draft, setDraft] = useState<SmartTrackingConfig | null>(null);

  const saved = configQuery.data ?? null;

  useEffect(() => {
    if (!saved) return;
    setDraft((current) =>
      current &&
      current.company_id === saved.company_id &&
      !sameSmartTrackingConfig(current, saved)
        ? current
        : saved,
    );
  }, [saved]);

  const config = draft ?? saved ?? DEFAULT_SMART_TRACKING_CONFIG;
  const isDirty = !!draft && !!saved && !sameSmartTrackingConfig(draft, saved);
  const shipments = shipmentsQuery.data?.data ?? [];

  const coverage = useMemo(() => {
    const total = shipments.length;
    const auto = shipments.filter(
      (shipment) => shipment.smart_tracking !== "manual",
    ).length;
    const autoPercent = total > 0 ? Math.round((auto / total) * 100) : 0;
    return {
      total,
      auto,
      manual: total - auto,
      autoPercent,
      manualPercent: 100 - autoPercent,
    };
  }, [shipments]);

  const automaticRules = SMART_TRACKING_RULE_IDS.filter(
    (id) => SMART_TRACKING_RULES[id].automatic,
  );
  const enabledAutoRules = automaticRules.filter((id) => config.rules[id]).length;

  const toggleMaster = () => setDraft({ ...config, enabled: !config.enabled });

  const toggleRule = (id: SmartTrackingRuleId) =>
    setDraft({
      ...config,
      rules: { ...config.rules, [id]: !config.rules[id] },
    });

  const discard = () => setDraft(saved);

  const submit = () => {
    const payload: SmartTrackingConfigInput = {
      enabled: config.enabled,
      rules: config.rules,
    };
    update.mutate(payload, {
      onSuccess: () => toast.success(t("smartTracking.messages.saved")),
      onError: () => toast.error(t("smartTracking.messages.saveError")),
    });
  };

  return (
    <div className="flex h-full flex-col">
      <SectionHeader
        title={t("smartTracking.title")}
        subtitle={t("smartTracking.subtitle")}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-content">
                {config.enabled
                  ? t("smartTracking.master.on")
                  : t("smartTracking.master.off")}
              </div>
              <div className="text-xs text-content-muted">
                {config.enabled
                  ? t("smartTracking.master.onHint")
                  : t("smartTracking.master.offHint")}
              </div>
            </div>
            <SmartSwitch
              checked={config.enabled}
              onChange={toggleMaster}
              disabled={configQuery.isLoading || update.isPending}
              label={t("smartTracking.master.label")}
            />
            {isDirty ? (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={discard}
                  disabled={update.isPending}
                >
                  {t("smartTracking.actions.discard")}
                </Button>
                <Button size="sm" onClick={submit} loading={update.isPending}>
                  {t("smartTracking.actions.save")}
                </Button>
              </div>
            ) : null}
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {configQuery.isLoading ? (
          <div className="flex min-h-64 items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          </div>
        ) : configQuery.isError ? (
          <EmptyState
            icon="ri-error-warning-line"
            title={t("smartTracking.error.title")}
            description={t("smartTracking.error.description")}
          />
        ) : (
          <div className="space-y-5">
            {isDirty ? (
              <div className="flex items-center gap-2 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-xs text-warning">
                <i className="ri-information-line text-base" />
                {t("smartTracking.actions.unsaved")}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-primary-500/30 bg-primary-500/10 p-4">
                <div className="text-xs font-medium text-primary-500">
                  {t("smartTracking.stats.auto")}
                </div>
                <div className="font-mono text-2xl font-bold text-primary-500">
                  {coverage.auto}
                </div>
                <div className="text-xs text-content-muted">
                  {t("smartTracking.stats.autoHint")}
                </div>
              </div>
              <div className="rounded-2xl border border-line bg-subtle p-4">
                <div className="text-xs font-medium text-content-muted">
                  {t("smartTracking.stats.manual")}
                </div>
                <div className="font-mono text-2xl font-bold text-content">
                  {coverage.manual}
                </div>
                <div className="text-xs text-content-muted">
                  {t("smartTracking.stats.manualHint")}
                </div>
              </div>
              <div className="rounded-2xl border border-info/30 bg-info/10 p-4">
                <div className="text-xs font-medium text-info">
                  {t("smartTracking.stats.activeRules")}
                </div>
                <div className="font-mono text-2xl font-bold text-info">
                  {enabledAutoRules}
                </div>
                <div className="text-xs text-content-muted">
                  {t("smartTracking.stats.activeRulesHint", {
                    total: automaticRules.length,
                  })}
                </div>
              </div>
            </div>

            <Card padding="none" className="overflow-hidden">
              <div className="border-b border-line px-5 py-4">
                <CardHeader
                  title={t("smartTracking.rules.title")}
                  subtitle={t("smartTracking.rules.subtitle")}
                />
              </div>
              <div className="divide-y divide-line">
                {SMART_TRACKING_RULE_IDS.map((id) => {
                  const rule = SMART_TRACKING_RULES[id];
                  return (
                    <div key={id} className="flex items-center gap-4 px-5 py-4">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                          rule.automatic
                            ? "bg-primary-500/15 text-primary-500"
                            : "bg-subtle text-content-muted"
                        }`}
                      >
                        <i className={`${rule.icon} text-lg`} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-content">
                            {t(`smartTracking.rules.items.${id}.trigger`)}
                          </span>
                          <i className="ri-arrow-right-line text-content-faint" />
                          <Badge tone={eventTypeTone(rule.eventType)}>
                            {rule.eventType
                              ? t(`eventTypes.${rule.eventType}`)
                              : t("smartTracking.rules.manualResult")}
                          </Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-content-muted">
                          {t(`smartTracking.rules.items.${id}.description`)}
                        </p>
                      </div>
                      <SmartSwitch
                        checked={config.rules[id]}
                        onChange={() => toggleRule(id)}
                        disabled={
                          !config.enabled || !rule.automatic || update.isPending
                        }
                        label={t(`smartTracking.rules.items.${id}.trigger`)}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card padding="none" className="overflow-hidden">
              <div className="border-b border-line px-5 py-4">
                <CardHeader
                  title={t("smartTracking.coverage.title")}
                  subtitle={t("smartTracking.coverage.subtitle", {
                    count: coverage.total,
                  })}
                />
              </div>

              <div className="p-5">
                {shipmentsQuery.isLoading ? (
                  <div className="flex min-h-64 items-center justify-center py-12">
                    <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
                  </div>
                ) : coverage.total === 0 ? (
                  <EmptyState
                    icon="ri-inbox-line"
                    title={t("smartTracking.coverage.empty.title")}
                    description={t("smartTracking.coverage.empty.description")}
                  />
                ) : (
                  <>
                    <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="rounded-2xl border border-primary-500/30 bg-primary-500/10 p-6">
                        <div className="flex items-center gap-2 text-xs font-semibold text-primary-500">
                          <i className="ri-flashlight-line text-base" />
                          {t("smartTracking.coverage.automatic")}
                        </div>
                        <div className="mt-2 font-mono text-3xl font-bold text-primary-500">
                          {coverage.auto}
                        </div>
                        <div className="text-xs text-content-secondary">
                          {t("smartTracking.coverage.automaticHint", {
                            percent: coverage.autoPercent,
                          })}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-line bg-subtle p-6">
                        <div className="flex items-center gap-2 text-xs font-semibold text-content-secondary">
                          <i className="ri-edit-line text-base" />
                          {t("smartTracking.coverage.manual")}
                        </div>
                        <div className="mt-2 font-mono text-3xl font-bold text-content">
                          {coverage.manual}
                        </div>
                        <div className="text-xs text-content-muted">
                          {t("smartTracking.coverage.manualHint", {
                            percent: coverage.manualPercent,
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="flex h-3 w-full overflow-hidden rounded-full bg-subtle">
                      <div
                        className="h-full bg-primary-500 transition-all"
                        style={{ width: `${coverage.autoPercent}%` }}
                      />
                      <div
                        className="h-full bg-content-faint transition-all"
                        style={{ width: `${coverage.manualPercent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-content-muted">
                      <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-primary-500" />
                        {t("smartTracking.coverage.autoLegend", {
                          count: coverage.auto,
                        })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-content-faint" />
                        {t("smartTracking.coverage.manualLegend", {
                          count: coverage.manual,
                        })}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
